import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import {
    alpha,
    Box,
    FormControlLabel,
    FormLabel,
    lighten,
    makeStyles,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
    withStyles,
} from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { HelpOutline } from '@material-ui/icons';

import { useTags } from '../../../services/FormQueryService';
import { NgpTagsField } from '../../form/NgpTagsField';
import { useArrayLoader } from '../../data-table/Loaders';
import DataTable from '../../data-table/DataTable';
import { useDataService } from '../../../services/ApiDataService';
import { buildNetTestNowAgentTagGridColumns, filterAgentsBySelectedTags, isWirelessAgent } from './NetTestNowHelpers';

const agentTagsUrl = '/ipm/v1/open/agents/tags';

export const StyledTooltip = withStyles((theme) => ({
    tooltip: {
        maxWidth: 360,
        padding: '8px',
        border: theme.palette.type === 'light' ? `1px solid ${theme.palette.text.primary}` : '',
        backgroundColor:
            theme.palette.type === 'light'
                ? theme.palette.background.paper
                : lighten(alpha(theme.palette.divider, 1), 0.85),
        color:
            theme.palette.type === 'light' ? theme.palette.text.primary : lighten(theme.palette.background.paper, 0.05),
    },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
    radioWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),

        '& .MuiFormLabel-root': {
            height: '16px',
        },

        '& .MuiFormControlLabel-root': {
            marginTop: 0,
        },
    },
    autoCompleteWrapper: {
        marginTop: 0,
    },
    matchedAgentsTableWrapper: {
        marginTop: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',

        '& .MuiTypography-subtitle1': {
            fontSize: '1.125rem',
        },

        '& .datatable': {
            width: '95%',
        },
    },
    matchedAgentsHeadingWrapper: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },
}));

export function NetTestNowAgentTagSelect({ setAgentSelectionIsDirty, interfaceType }) {
    const styles = useStyles();
    const [agentList, setAgentList] = useState([]);
    const [matchingAgents, setMatchingAgents] = useState([]);
    const loader = useArrayLoader(matchingAgents);
    const { register, setValue, watch } = useFormContext();
    const tagLogic = watch('tagLogic');
    const selectedTags = watch('tags');
    const { tags } = useTags({ url: agentTagsUrl });
    const dataService = useDataService();

    useEffect(() => {
        (async () => {
            const { data } = await dataService.get('/query/admin/agents', { limit: 1000 });
            setAgentList(data.filter((agentData) => agentData.status.toLowerCase() === 'running'));
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const wirelessAgentList = agentList?.filter((agent) => isWirelessAgent(agent));
        if (interfaceType === 'wireless') {
            setMatchingAgents(filterAgentsBySelectedTags(wirelessAgentList, selectedTags, tagLogic));
        } else {
            setMatchingAgents(filterAgentsBySelectedTags(agentList, selectedTags, tagLogic));
        }

        if (matchingAgents) {
            setAgentSelectionIsDirty(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagLogic, agentList, selectedTags, interfaceType]);

    return (
        <>
            <div className={styles.radioWrapper}>
                <FormLabel component="legend">
                    <Trans>Tags: </Trans>
                </FormLabel>
                <RadioGroup
                    {...register('tagLogic')}
                    row
                    value={tagLogic}
                    onChange={(e) => setValue('tagLogic', e.target.value)}
                >
                    <FormControlLabel value="any" label={t`Any`} control={<Radio />} />
                    <FormControlLabel value="all" label={t`All`} control={<Radio />} />
                </RadioGroup>
            </div>
            <NgpTagsField
                className={styles.autoCompleteWrapper}
                fieldName="tags"
                label={t`Tags`}
                options={tags}
                allowNewTags={false}
            />
            <div className={styles.matchedAgentsTableWrapper}>
                <div className={styles.matchedAgentsHeadingWrapper}>
                    <Typography variant="subtitle1">Matching Agents</Typography>
                    <StyledTooltip
                        placement="right"
                        title={
                            <Box sx={{ '& *': { fontSize: '12px' } }}>
                                <Typography>
                                    <Trans>
                                        These are the agents that will run the test based on your selected tags and tag
                                        logic
                                    </Trans>
                                </Typography>
                            </Box>
                        }
                    >
                        <HelpOutline fontSize="small" />
                    </StyledTooltip>
                </div>
                <DataTable
                    loader={loader}
                    columns={buildNetTestNowAgentTagGridColumns()}
                    disableCSVExport
                    disableColumnRemover
                    visibleRows={6}
                />
            </div>
        </>
    );
}
